import { useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import MarkdownRenderer from './MarkdownRenderer';

interface FAQItem {
  '@type': 'Question';
  name: string;
  acceptedAnswer: {
    '@type': 'Answer';
    text: string;
  };
}

interface FAQAccordionProps {
  faqs: FAQItem[];
  className?: string;
  campaign?: string; // Optional campaign identifier for analytics (e.g., 'ltd-parents')
}

/**
 * FAQ Accordion Component
 * 
 * SEO Best Practices Followed:
 * - ✅ Visible Content: All answers are visible by default (required by Google)
 * - ✅ Collapsible UX: Users can collapse for better navigation on long lists
 * - ✅ Semantic HTML: Uses proper heading hierarchy (H2 for section, H3 for questions)
 * - ✅ Accessibility: ARIA labels, keyboard navigation, screen reader support
 * - ✅ Content Matching: Visible content matches schema.org structured data exactly
 * - ✅ Internal Linking: Supports HTML links in answers (best practice)
 * - ✅ Dedicated Section: Clear "FAQs" heading with proper structure
 * 
 * Note: All FAQ answers are visible by default to meet Google's requirement.
 * Users can collapse individual items for better UX, but content remains in DOM.
 */
export default function FAQAccordion({ faqs, className = '', campaign }: FAQAccordionProps) {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set()); // All closed by default
  const [allCollapsed, setAllCollapsed] = useState(true);

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
    setAllCollapsed(false);
  };

  const toggleAll = () => {
    if (allCollapsed || openItems.size === 0) {
      // Expand all
      setOpenItems(new Set(faqs.map((_, i) => i)));
      setAllCollapsed(false);
    } else {
      // Collapse all
      setOpenItems(new Set());
      setAllCollapsed(true);
    }
  };

  if (!faqs || faqs.length === 0) {
    return null;
  }

  const allExpanded = openItems.size === faqs.length;

  return (
    <section className={`faq-accordion ${className}`} aria-labelledby="faq-heading">
      <div className="flex items-center justify-between mb-8">
        <h2 id="faq-heading" className="text-3xl font-bold text-gray-900 text-center">
          Frequently Asked Questions
        </h2>
        {faqs.length > 3 && (
          <button
            onClick={toggleAll}
            className="text-sm text-primary hover:text-primary/80 font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded px-3 py-1"
            type="button"
            aria-label={allExpanded ? 'Collapse all FAQs' : 'Expand all FAQs'}
          >
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </button>
        )}
      </div>
      <div className="space-y-4" role="list">
        {faqs.map((faq, index) => {
          const isOpen = openItems.has(index);
          
          return (
            <article
              key={index}
              className="bg-gray-50 rounded-lg border border-gray-200 transition-all"
              role="listitem"
              itemScope
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${index}`}
                type="button"
                data-umami-event={campaign ? `LTD-Parents-FAQ-Clicked` : 'FAQ-Clicked'}
                data-umami-event-action={isOpen ? 'close' : 'open'}
                data-umami-event-question={faq.name}
              >
                <h3 
                  className="text-lg font-semibold text-gray-900 pr-4 flex-1"
                  itemProp="name"
                  id={`faq-question-${index}`}
                >
                  {faq.name}
                </h3>
                <div className="flex items-center gap-2 flex-shrink-0" aria-hidden="true">
                  {isOpen ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </div>
              </button>
              
              {/* Divider between question and answer - 1px only */}
              {isOpen && (
                <div className="border-t border-gray-300 mx-6"></div>
              )}
              
              {/* Answer - Always in DOM and accessible
                  Visible by default (all expanded) to meet Google's requirement
                  Users can collapse for better UX, but content remains accessible */}
              <div
                id={`faq-answer-${index}`}
                className={`px-6 pb-4 text-gray-700 transition-opacity duration-300 ${
                  isOpen ? 'opacity-100 pt-4' : 'opacity-0 max-h-0 overflow-hidden p-0'
                }`}
                itemScope
                itemType="https://schema.org/Answer"
                itemProp="acceptedAnswer"
                role="region"
                aria-labelledby={`faq-question-${index}`}
                aria-hidden={!isOpen}
                style={isOpen ? { display: 'block', overflow: 'visible' } : { display: 'none' }}
              >
                {/* Use MarkdownRenderer to properly handle HTML tables and other content */}
                <div itemProp="text">
                  <MarkdownRenderer content={faq.acceptedAnswer.text} className="prose-sm" />
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

